import {
    Table,
    Input,
    Button,
    Space,
    Tooltip,
    Popconfirm,
    Tag,
    Select,
} from "antd";
import {
    SearchOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
} from "@ant-design/icons";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchAllClients,
    deleteClient,
    createNewClient,
    updateExistingClient,
} from "../../Actions/client.action";
import ClientFormModal from "./ClientFormModal";
import ClientAiPolicyPreview from "./ClientAiPolicyPreview";
import { useTranslation } from "react-i18next";
import "./clients.css";

function sanitizeClientFormValues(formValues = {}) {
    const { subscription_plan, ...safeValues } = formValues;
    return safeValues;
}

const ClientTablePage = () => {
    const { t } = useTranslation("clients");
    const dispatch = useDispatch();
    const { clients = [], loading } = useSelector((state) => state.clients);
    const userState = useSelector((state) => state.users);
    const currentUser = userState?.currentUser?.role ? userState.currentUser : userState?.user;
    const isSuperAdmin = currentUser?.role === "SUPER_ADMIN";

    const [search, setSearch] = useState("");
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [viewingClient, setViewingClient] = useState(null);

    useEffect(() => {
        dispatch(fetchAllClients());
    }, [dispatch]);

    const filteredData = clients.filter((client) => {
        const matchesSearch = client.name.toLowerCase().includes(search.toLowerCase());
        const matchesStatus =
            selectedStatus === null ||
            selectedStatus === undefined ||
            (selectedStatus === "active" && client.is_active) ||
            (selectedStatus === "inactive" && !client.is_active);

        return matchesSearch && matchesStatus;
    });

    const openCreateModal = () => {
        setEditingClient(null);
        setIsModalVisible(true);
    };

    const openEditModal = (client) => {
        setEditingClient(client);
        setIsModalVisible(true);
    };

    const openDetailDrawer = (client) => {
        setViewingClient(client);
    };

    const closeDetailDrawer = () => {
        setViewingClient(null);
    };

    const closeModal = () => {
        setIsModalVisible(false);
        setEditingClient(null);
    };

    const handleSubmitClient = async (formValues) => {
        try {
            const safeFormValues = sanitizeClientFormValues(formValues);
            let result;
            if (editingClient) {
                result = await dispatch(updateExistingClient(editingClient.id, safeFormValues));
            } else {
                result = await dispatch(createNewClient(safeFormValues));
            }

            if (result?.success) {
                closeModal();
            } else {
                console.error("Erreur:", result?.error || "Operation failed");
            }

            return result?.success;
        } catch (err) {
            console.error("Exception:", err);
            return false;
        }
    };

    const columns = [
        {
            title: t("table.name"),
            dataIndex: "name",
            width: 280,
            render: (text, record) => (
                <span className="clients-table__name">
                    <span>{text || "-"}</span>
                    {record.is_protected && (
                        <Tag className="clients-status clients-status--protected">
                            {t("tags.protected")}
                        </Tag>
                    )}
                </span>
            ),
        },
        {
            title: t("table.email"),
            dataIndex: "contact_email",
            width: 280,
            render: (email) => <span className="clients-table__muted">{email || "-"}</span>,
        },
        {
            title: t("table.industry"),
            dataIndex: "industry",
            width: 220,
            render: (industry) => <span>{industry || "-"}</span>,
        },
        {
            title: t("table.workspacePlan"),
            key: "workspace_plan",
            width: 210,
            render: () => (
                <span className="clients-workspace-plan-cell">
                    <Tag className="clients-plan">{t("table.workspacePlanManaged")}</Tag>
                    <small>{t("table.workspacePlanHint")}</small>
                </span>
            ),
        },
        {
            title: t("table.status"),
            dataIndex: "is_active",
            width: 130,
            render: (active) => (
                <Tag className={active ? "clients-status clients-status--active" : "clients-status clients-status--inactive"}>
                    {active ? t("status.active") : t("status.inactive")}
                </Tag>
            ),
        },
        {
            title: t("table.actions"),
            key: "actions",
            width: 110,
            fixed: "right",
            render: (_, record) => (
                <Space size={4}>
                    {isSuperAdmin && (
                        <Tooltip title={t("actions.view")}>
                            <Button
                                type="text"
                                className="clients-action-btn"
                                icon={<EyeOutlined />}
                                onClick={() => openDetailDrawer(record)}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title={t("actions.edit")}>
                        <Button
                            type="text"
                            className="clients-action-btn"
                            icon={<EditOutlined />}
                            onClick={() => openEditModal(record)}
                        />
                    </Tooltip>
                    {record.is_protected ? (
                        <Tooltip title={t("actions.protectedDelete")}>
                            <Button
                                type="text"
                                className="clients-action-btn clients-action-btn--danger"
                                icon={<DeleteOutlined />}
                                danger
                                disabled
                            />
                        </Tooltip>
                    ) : (
                        <Popconfirm
                            title={t("actions.confirmDelete")}
                            okText={t("actions.confirm")}
                            cancelText={t("actions.cancel")}
                            onConfirm={() => dispatch(deleteClient(record.id))}
                        >
                            <Tooltip title={t("actions.delete")}>
                                <Button
                                    type="text"
                                    className="clients-action-btn clients-action-btn--danger"
                                    icon={<DeleteOutlined />}
                                    danger
                                />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="clients-page">
            <div className="clients-page__header">
                <p className="clients-page__subtitle">
                    {filteredData.length} {t("labels.displayed")}
                </p>
                <div className="clients-page__actions">
                    <Button
                        type="primary"
                        className="clients-page__primary-btn verdictia-primary-btn"
                        icon={<PlusOutlined />}
                        onClick={openCreateModal}
                    >
                        {t("actions.add")}
                    </Button>
                </div>
            </div>

            <div className="clients-page__filters">
                <div className="clients-page__filter clients-page__filter--search">
                    <Input
                        placeholder={t("search.placeholder")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        prefix={<SearchOutlined />}
                        allowClear
                    />
                </div>
                <div className="clients-page__filter">
                    <Select
                        allowClear
                        style={{ width: "100%" }}
                        placeholder={t("search.filterStatus")}
                        value={selectedStatus}
                        onChange={(value) => setSelectedStatus(value)}
                        options={[
                            { label: t("status.active"), value: "active" },
                            { label: t("status.inactive"), value: "inactive" },
                        ]}
                    />
                </div>
            </div>

            <div className="clients-page__table-card">
                <Table
                    columns={columns}
                    dataSource={filteredData}
                    loading={loading}
                    size="middle"
                    pagination={{ pageSize: 8, showSizeChanger: false }}
                    scroll={{ x: 1180 }}
                    tableLayout="fixed"
                    rowKey="id"
                    rowClassName={(record) => (!record.is_active ? "inactive-row" : "")}
                />
            </div>

            <ClientFormModal
                open={isModalVisible}
                onClose={closeModal}
                onSubmit={handleSubmitClient}
                initialValues={editingClient}
            />
            <ClientAiPolicyPreview
                open={!!viewingClient}
                onClose={closeDetailDrawer}
                client={viewingClient}
                isSuperAdmin={isSuperAdmin}
            />
        </div>
    );
};

export default ClientTablePage;
